import * as Crypto from 'expo-crypto';
import { normalizeContactDetails } from './contactDetails';
import config from '../config';
import { openComposer } from '../store/reducers/composer';

export const validOrganizationEmail = (value: string) => /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(value.trim());

/** An editable request, never a claim that the organization has changed anything. */
export function planEmailDraft(plan: any, user: any) {
  if (!plan?.mnemonic || !/^[a-z0-9-]+$/i.test(plan.mnemonic)) return null;
  const address = `${plan.mnemonic}@${config.mailDomain}`;
  const person = [plan.first_name, plan.last_name].filter(Boolean).join(' ').trim();
  if (!person) return null;
  const sender = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return {
    subject: 'Please update the email address on file',
    body: `Hello,\n\nPlease update the email address on file for ${person} to:\n${address}\n\nPlease use this address for all future email correspondence. This is a shared care-plan inbox that helps us keep everything together. Please keep existing phone and postal contact details unchanged.\n\nPlease reply to confirm the update, or let me know if you need a form or identity verification through your usual secure process.\n\nThank you${sender ? `,\n${sender}` : '.'}`,
    emailUpdateRequest: { id: Crypto.randomUUID(), address },
  };
}

export const openPlanEmailRequest = (projectId: number | string, event?: any) => (dispatch: any, getState: () => any) => {
  const state = getState();
  if (state.featureFlags?.flags?.plan_email_requests_enabled !== true) return;
  const initial = planEmailDraft(state.projects?.list?.[String(projectId)], state.authentication?.user);
  const recipient = organizationForVisit(event, state.contacts?.list?.[String(projectId)] || []);
  if (initial && recipient) Object.assign(initial, { to: recipient.email });
  if (initial) dispatch(openComposer({ projectId, inboxEmail: initial.emailUpdateRequest.address, initial }));
};

export function previousEmailRequest(messages: any[], email: string, address: string) {
  return messages.find((message) => message.email_update_request?.address === address
    && String(message.to_email || '').toLowerCase() === email.trim().toLowerCase());
}

/** Only a saved contact explicitly linked by id or organizer address is preselected. */
export function organizationForVisit(event: any, contacts: any[]) {
  if (!event) return null;
  const organizer = String(event.event_data?.organizer?.email || '').toLowerCase();
  const matches = contacts.map(normalizeContactDetails).filter(contact => validOrganizationEmail(String(contact.email || ''))
    && ((event.contact_id != null && String(contact.id) === String(event.contact_id))
      || (!!organizer && String(contact.email).toLowerCase() === organizer)));
  return matches.length === 1 ? matches[0] : null;
}

export function shouldSuggestEmailRequest(event: any, contacts: any[], messages: any[], calls: any[] = [], planAddress?: string) {
  const contact = organizationForVisit(event, contacts);
  if (!contact) return true; // The composer asks the user to choose; it never guesses from a visit title.
  const address = String(contact.email).toLowerCase();
  if (planAddress && calls.some(call => String(call.contact_id) === String(contact.id)
    && String(call.address).toLowerCase() === planAddress.toLowerCase()
    && (call.outcome === 'confirmed_by_office' || call.phase !== 'ended'))) return false;
  return !messages.some(message => (message.email_update_request && String(message.to_email).toLowerCase() === address
    && (!planAddress || String(message.email_update_request.address).toLowerCase() === planAddress.toLowerCase()))
    || (!message.from_cognito && String(message.from_email).toLowerCase() === address
      && (!planAddress || String(message.to_email).toLowerCase() === planAddress.toLowerCase())));
}
